/**
 * CORPORATE EMPLOYEE endpoints (Personel / Kimlik Kartı / Pasaport Belgesi) — migration
 * guide Bölüm 3.9. See api/types/corporateEmployee.ts for field-shape notes and confirmed
 * real-API quirks (KvkkAcceptedAt DateTime-default bug, Edit-acts-as-insert for sub-resources,
 * Delete requiring employeeID, DeleteCorporateEmployee's server bug, etc.) — every route
 * below is confirmed reachable via curl against the live API with a real test session.
 */
import { apiPost, apiPostMultipart, apiPostSession } from '../client';
import { ApiError, type ResponseBase } from '../types/common';
import type {
  AcceptKvkkRequest,
  AcceptKvkkResponse,
  CorporateEmployee,
  CorporateEmployeeListResponse,
  CorporateEmployeeQuery,
  CorporateIDCard,
  CorporateIDCardListResponse,
  CorporateLoyalty,
  CorporateLoyaltyListResponse,
  CorporatePassportDocument,
  CorporatePassportDocumentListResponse,
  EmployeeCostCenterOptionListResponse,
  EmployeePolicyOptionListResponse,
  ImportCorporateEmployeesResponse,
  UpdateEmployeeManagerRequest,
} from '../types/corporateEmployee';

/* ------------------------------- Employee ------------------------------- */

export function getCorporateEmployees(): Promise<CorporateEmployeeListResponse> {
  return apiPostSession<CorporateEmployeeListResponse>('CorporateEmployee/GetCorporateEmployees');
}

export function getCorporateEmployeeById(employeeID: number): Promise<CorporateEmployeeListResponse> {
  return apiPost<{ corporateEmployee: Partial<CorporateEmployee> }, CorporateEmployeeListResponse>(
    'CorporateEmployee/GetCorporateEmployeeById',
    { corporateEmployee: { employeeID } }
  );
}

/** Previously threw a server-side NullReferenceException for every input (see type-file
 *  header for the original repro notes) — now fixed backend-side and used for
 *  search-as-you-type personnel selection (`PersonnelAutocomplete`), same idiom as
 *  `Airport/GetWithQuery`. */
export function getCorporateEmployeeByQuery(
  query: CorporateEmployeeQuery
): Promise<CorporateEmployeeListResponse> {
  return apiPost<{ corporateEmployee: CorporateEmployeeQuery }, CorporateEmployeeListResponse>(
    'CorporateEmployee/GetCorporateEmployeeByQuery',
    { corporateEmployee: query }
  );
}

export function addCorporateEmployee(
  employee: Omit<CorporateEmployee, 'employeeID'>
): Promise<CorporateEmployeeListResponse> {
  return apiPost<{ corporateEmployee: Omit<CorporateEmployee, 'employeeID'> }, CorporateEmployeeListResponse>(
    'CorporateEmployee/AddCorporateEmployee',
    { corporateEmployee: employee }
  );
}

/** MUST be called with the full employee object (including `kvkkAcceptedAt`/`dateOfBirth`
 *  round-tripped from the last Get) — omitting DateTime fields defaults them to
 *  `0001-01-01`, which blows up EF's save with a generic "error occurred while updating the
 *  entries" — see type-file header. Note: `managerID` is ignored here — use
 *  `updateEmployeeManager` (its own route, with server-side cycle guards). */
export function editCorporateEmployee(employee: CorporateEmployee): Promise<CorporateEmployeeListResponse> {
  return apiPost<{ corporateEmployee: CorporateEmployee }, CorporateEmployeeListResponse>(
    'CorporateEmployee/EditCorporateEmployee',
    { corporateEmployee: employee }
  );
}

/** `CorporateEmployee/UpdateManager` — sets an employee's `ManagerID` (org-chart drag-drop).
 *  Real route per the code-traced `Docs/api/05-corporate.md:631-646`; the server rejects
 *  self / own-subtree / non-existent manager ("Personel kendi alt ağacının altına veya
 *  geçersiz bir yöneticiye taşınamaz."). `managerId: null` = top of tree. */
export function updateEmployeeManager(
  body: UpdateEmployeeManagerRequest
): Promise<CorporateEmployeeListResponse> {
  return apiPost<UpdateEmployeeManagerRequest, CorporateEmployeeListResponse>(
    'CorporateEmployee/UpdateManager',
    body
  );
}

/** Confirmed server-side bug: returns `{"Non-static method requires a target."}` for every
 *  payload tried, independent of shape — see type-file header. Wired per contract; UI
 *  surfaces whatever error the backend returns via ApiErrorAlert. */
export function deleteCorporateEmployee(employee: CorporateEmployee): Promise<CorporateEmployeeListResponse> {
  return apiPost<{ corporateEmployee: CorporateEmployee }, CorporateEmployeeListResponse>(
    'CorporateEmployee/DeleteCorporateEmployee',
    { corporateEmployee: employee }
  );
}

/** Accepts/records KVKK for the *session's own* linked employee only — no `EmployeeID` field
 *  exists on this request. Not usable as an admin action on an arbitrary employee. */
export function acceptKvkk(body: AcceptKvkkRequest): Promise<AcceptKvkkResponse> {
  return apiPost<AcceptKvkkRequest, AcceptKvkkResponse>('CorporateEmployee/AcceptKvkk', body);
}

/** multipart/form-data — field name `file` confirmed via curl (an unrecognized field name
 *  falls through to a generic "Excel dosyası boş." error instead of attempting to parse). */
export function importCorporateEmployeesFromExcel(file: File): Promise<ImportCorporateEmployeesResponse> {
  const formData = new FormData();
  formData.append('file', file);
  return apiPostMultipart<ImportCorporateEmployeesResponse>(
    'CorporateEmployee/ImportCorporateEmployeesFromExcel',
    formData
  );
}

/** The real API returns `hasError:true` with this exact message when an employee simply has
 *  no card/passport on file — NOT a real failure. See type-file header. */
const NO_RECORDS_MESSAGE = 'Personel bilgisi kayıtlıdır.';

function isNoRecordsError(err: unknown): boolean {
  return err instanceof ApiError && err.message === NO_RECORDS_MESSAGE;
}

/* ------------------------------- ID Card ------------------------------- */

export async function getCorporateIdCardByEmployeeId(employeeID: number): Promise<CorporateIDCardListResponse> {
  try {
    return await apiPost<{ corporateIDCard: Partial<CorporateIDCard> }, CorporateIDCardListResponse>(
      'CorporateIDCard/GetCorporateIDByEmployeeId',
      { corporateIDCard: { employeeID } }
    );
  } catch (err) {
    if (isNoRecordsError(err)) return { hasError: false, corporateIDCard: [] };
    throw err;
  }
}

export function addCorporateIdCard(card: Omit<CorporateIDCard, 'cardID'>): Promise<CorporateIDCardListResponse> {
  return apiPost<{ corporateIDCard: Omit<CorporateIDCard, 'cardID'> }, CorporateIDCardListResponse>(
    'CorporateIDCard/AddCorporateIDCard',
    { corporateIDCard: card }
  );
}

/** Confirmed to behave as delete-old+insert-new server-side — the returned/refetched card
 *  comes back under a NEW `cardID`. Refetch after calling this. */
export function editCorporateIdCard(card: CorporateIDCard): Promise<CorporateIDCardListResponse> {
  return apiPost<{ corporateIDCard: CorporateIDCard }, CorporateIDCardListResponse>(
    'CorporateIDCard/EditCorporateIDCard',
    { corporateIDCard: card }
  );
}

/** Requires BOTH `cardID` and `employeeID` — id-only 400s with "Personel bilgileri bulunamadı." */
export function deleteCorporateIdCard(card: Pick<CorporateIDCard, 'cardID' | 'employeeID'>): Promise<ResponseBase> {
  return apiPost<{ corporateIDCard: Pick<CorporateIDCard, 'cardID' | 'employeeID'> }, ResponseBase>(
    'CorporateIDCard/DeleteCorporateIDCard',
    { corporateIDCard: card }
  );
}

/* --------------------------- Passport Document --------------------------- */

export async function getCorporatePassportDocumentByEmployeeId(
  employeeID: number
): Promise<CorporatePassportDocumentListResponse> {
  try {
    return await apiPost<
      { corporatePassportDocument: Partial<CorporatePassportDocument> },
      CorporatePassportDocumentListResponse
    >('CorporatePassportDocument/GetCorporatePassportDocumentByEmployeeId', {
      corporatePassportDocument: { employeeID },
    });
  } catch (err) {
    if (isNoRecordsError(err)) return { hasError: false, corporatePassportDocument: [] };
    throw err;
  }
}

export function addCorporatePassportDocument(
  passport: Omit<CorporatePassportDocument, 'passportID'>
): Promise<CorporatePassportDocumentListResponse> {
  return apiPost<
    { corporatePassportDocument: Omit<CorporatePassportDocument, 'passportID'> },
    CorporatePassportDocumentListResponse
  >('CorporatePassportDocument/AddCorporatePassportDocument', { corporatePassportDocument: passport });
}

/** Confirmed to behave as delete-old+insert-new server-side — the returned/refetched
 *  passport comes back under a NEW `passportID`. Refetch after calling this. */
export function editCorporatePassportDocument(
  passport: CorporatePassportDocument
): Promise<CorporatePassportDocumentListResponse> {
  return apiPost<{ corporatePassportDocument: CorporatePassportDocument }, CorporatePassportDocumentListResponse>(
    'CorporatePassportDocument/EditCorporatePassportDocument',
    { corporatePassportDocument: passport }
  );
}

/** Requires BOTH `passportID` and `employeeID` — id-only 400s with "Personel bilgileri bulunamadı." */
export function deleteCorporatePassportDocument(
  passport: Pick<CorporatePassportDocument, 'passportID' | 'employeeID'>
): Promise<ResponseBase> {
  return apiPost<{ corporatePassportDocument: Pick<CorporatePassportDocument, 'passportID' | 'employeeID'> }, ResponseBase>(
    'CorporatePassportDocument/DeleteCorporatePassportDocument',
    { corporatePassportDocument: passport }
  );
}

/* --------------------------- Lookups (dropdowns) ---------------------------
 * Minimal, locally-scoped read-only lookups for the employee form's Politika / Masraf
 * Merkezi selects — intentionally separate from the sibling org-structure module's
 * api/endpoints/corporate.ts (do not import from there; see module scope notes).
 */

export function getPolicyOptions(): Promise<EmployeePolicyOptionListResponse> {
  return apiPostSession<EmployeePolicyOptionListResponse>('Policy/GetPolicies');
}

export function getCostCenterOptions(): Promise<EmployeeCostCenterOptionListResponse> {
  return apiPostSession<EmployeeCostCenterOptionListResponse>('CostCenter/GetCostCenters');
}

/* --------------------------- Loyalty programs (backend Faz F, swagger-confirmed) --------------------------- */

export function getEmployeeLoyalty(employeeID: number): Promise<CorporateLoyaltyListResponse> {
  return apiPost<{ corporateLoyalty: { employeeID: number } }, CorporateLoyaltyListResponse>(
    'CorporateLoyalty/GetLoyaltyByEmployeeId',
    { corporateLoyalty: { employeeID } }
  );
}

export function saveEmployeeLoyalty(loyalty: CorporateLoyalty): Promise<CorporateLoyaltyListResponse> {
  return apiPost<{ corporateLoyalty: CorporateLoyalty }, CorporateLoyaltyListResponse>(
    'CorporateLoyalty/AddEditLoyalty',
    { corporateLoyalty: loyalty }
  );
}

export function deleteEmployeeLoyalty(
  loyalty: Pick<CorporateLoyalty, 'loyaltyProgramID' | 'employeeID'>
): Promise<ResponseBase> {
  return apiPost<
    { corporateLoyalty: Pick<CorporateLoyalty, 'loyaltyProgramID' | 'employeeID'> },
    ResponseBase
  >('CorporateLoyalty/DeleteLoyalty', { corporateLoyalty: loyalty });
}
